import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Pin, Eye, EyeOff, Megaphone, CalendarDays, AlertTriangle, X, Users } from "lucide-react";
import PhotoUploader from "@/components/PhotoUploader";

const KINDS = [
  { key: "news", label: "Actualités", icon: Megaphone, singular: "actualité" },
  { key: "event", label: "Événements", icon: CalendarDays, singular: "événement" },
  { key: "comm", label: "Communications importantes", icon: AlertTriangle, singular: "communication" },
  { key: "team", label: "Vie de l'équipe", icon: Users, singular: "info équipe" },
];
const TEAM_LABEL = { anniversaire: "Anniversaire", nouveau: "Nouveau collaborateur", mobilite: "Mobilité", bravo: "Félicitations" };
const LEVEL_STYLE = { info: "bg-blue-100 text-blue-700", important: "bg-orange-100 text-orange-700", urgent: "bg-rose-100 text-rose-700" };
const EMPTY = { title: "", summary: "", body: "", category: "", cover_url: "", published: true, pinned: false, date: "", end_date: "", time: "", location: "", level: "info", starts_at: "", ends_at: "", link: "", service: "", people: [] };

export default function AdminIntranet() {
  const [kind, setKind] = useState("news");
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [users, setUsers] = useState([]);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/intranet/${kind}`, { params: { all: true } }); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  useEffect(() => { api.get("/intranet/meta").then(r => setMeta(r.data)).catch(() => {}); api.get("/users").then(r => setUsers(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [kind]);

  const remove = async (it) => {
    if (!confirm(`Supprimer « ${it.title} » ?`)) return;
    try { await api.delete(`/intranet/${kind}/${it.id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (it, field) => {
    try { await api.patch(`/intranet/${kind}/${it.id}`, { [field]: !it[field] }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const cur = KINDS.find(k => k.key === kind);
  return (
    <div className="space-y-4" data-testid="admin-intranet">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="inline-flex bg-slate-100 rounded-lg p-1">
          {KINDS.map(k => (
            <button key={k.key} onClick={() => setKind(k.key)} data-testid={`admin-intranet-tab-${k.key}`}
                    className={`px-4 py-2 rounded-md text-xs font-bold flex items-center gap-1.5 transition-colors ${kind === k.key ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>
              <k.icon className="w-3.5 h-3.5"/>{k.label}
            </button>
          ))}
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="admin-intranet-new"
                className="px-4 py-2 rounded-lg bg-[#1d4ed8] hover:bg-blue-800 text-white text-sm font-semibold flex items-center gap-1.5">
          <Plus className="w-4 h-4"/> Nouvelle {cur.singular}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400"/></div> :
         items.length === 0 ? <div className="py-16 text-center text-sm text-slate-400" data-testid="admin-intranet-empty">Aucune {cur.singular} pour l'instant.</div> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-400">
              <tr><th className="text-left px-4 py-2">Titre</th><th className="text-left px-4 py-2">{kind === "event" ? "Date" : kind === "comm" ? "Service" : kind === "team" ? "Type" : "Catégorie"}</th><th className="text-left px-4 py-2">Auteur</th><th className="text-left px-4 py-2">État</th><th className="px-4 py-2"/></tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`admin-intranet-row-${it.id}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      {it.pinned && <Pin className="w-3.5 h-3.5 text-orange-500"/>}
                      <span className="font-semibold text-slate-800">{it.title}</span>
                    </div>
                    {it.summary && <div className="text-xs text-slate-500 truncate max-w-md">{it.summary}</div>}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">
                    {kind === "event" ? `${it.date}${it.time ? " · " + it.time : ""}` :
                     kind === "comm" ? <span className="flex items-center gap-1.5">{it.service || "—"}<span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${LEVEL_STYLE[it.level] || ""}`}>{it.level}</span></span> :
                     kind === "team" ? (TEAM_LABEL[it.category] || "—") : (it.category || "—")}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-slate-600">{it.author_name}</td>
                  <td className="px-4 py-2.5">
                    <button onClick={() => toggle(it, "published")} data-testid={`admin-intranet-toggle-pub-${it.id}`}
                            className={`text-[11px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${it.published ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {it.published ? <Eye className="w-3 h-3"/> : <EyeOff className="w-3 h-3"/>}{it.published ? "Publié" : "Brouillon"}
                    </button>
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button onClick={() => toggle(it, "pinned")} title="Épingler" className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500" data-testid={`admin-intranet-pin-${it.id}`}><Pin className="w-4 h-4"/></button>
                    <button onClick={() => setEditing({ ...EMPTY, ...it })} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500" data-testid={`admin-intranet-edit-${it.id}`}><Pencil className="w-4 h-4"/></button>
                    <button onClick={() => remove(it)} className="p-1.5 rounded-md hover:bg-rose-50 text-rose-500" data-testid={`admin-intranet-delete-${it.id}`}><Trash2 className="w-4 h-4"/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && <ContentForm kind={kind} meta={meta} users={users} initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return <label className={`block ${className}`}><span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</span><div className="mt-1">{children}</div></label>;
}
const inp = "w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-[#1d4ed8] focus:ring-2 focus:ring-blue-100";

function ContentForm({ kind, meta, users = [], initial, onClose, onSaved }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const isEdit = !!initial.id;
  const singular = KINDS.find(k => k.key === kind).singular;

  const save = async () => {
    if (!f.title.trim()) return toast.error("Titre requis");
    if (kind === "event" && !f.date) return toast.error("Date requise");
    setSaving(true);
    const payload = Object.fromEntries(Object.entries(f).filter(([k]) => k in EMPTY).map(([k, v]) => [k, v === "" ? null : v]));
    payload.people = f.people || [];
    payload.published = !!f.published; payload.pinned = !!f.pinned;
    try {
      if (isEdit) await api.patch(`/intranet/${kind}/${initial.id}`, payload);
      else await api.post(`/intranet/${kind}`, payload);
      toast.success(isEdit ? "Modifié" : "Créé"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const cats = kind === "news" ? meta?.news_categories : kind === "event" ? meta?.event_categories : null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-intranet-form">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-bold text-slate-900">{isEdit ? "Modifier" : "Nouvelle"} {singular}</h3>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-slate-100" data-testid="admin-intranet-form-close"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Titre" className="sm:col-span-2"><input className={inp} value={f.title} onChange={e => set("title", e.target.value)} data-testid="intranet-form-title"/></Field>
          {kind === "team" && (
            <Field label="Type">
              <select className={inp} value={f.category || ""} onChange={e => set("category", e.target.value)} data-testid="intranet-form-team-type">
                <option value="">—</option>{(meta?.team_types || []).map(c => <option key={c} value={c}>{TEAM_LABEL[c]}</option>)}
              </select>
            </Field>
          )}
          {kind === "team" && (
            <Field label="Personnes concernées">
              <select multiple className={`${inp} h-32`} value={f.people || []} onChange={e => set("people", Array.from(e.target.selectedOptions).map(o => o.value))} data-testid="intranet-form-people">
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
          )}
          {kind !== "comm" && kind !== "team" && (
            <Field label="Catégorie">
              <select className={inp} value={f.category || ""} onChange={e => set("category", e.target.value)} data-testid="intranet-form-category">
                <option value="">—</option>{(cats || []).map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>
          )}
          {kind === "comm" && (
            <Field label="Service">
              <select className={inp} value={f.service || ""} onChange={e => set("service", e.target.value)} data-testid="intranet-form-service">
                <option value="">—</option>{(meta?.comm_services || []).map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>
          )}
          {kind === "comm" && (
            <Field label="Niveau">
              <select className={inp} value={f.level || "info"} onChange={e => set("level", e.target.value)} data-testid="intranet-form-level">
                {(meta?.comm_levels || ["info", "important", "urgent"]).map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </Field>
          )}
          {kind === "event" && (<>
            <Field label="Date"><input type="date" className={inp} value={f.date || ""} onChange={e => set("date", e.target.value)} data-testid="intranet-form-date"/></Field>
            <Field label="Heure"><input type="time" className={inp} value={f.time || ""} onChange={e => set("time", e.target.value)} data-testid="intranet-form-time"/></Field>
            <Field label="Lieu"><input className={inp} value={f.location || ""} onChange={e => set("location", e.target.value)} data-testid="intranet-form-location"/></Field>
          </>)}
          {kind === "comm" && (<>
            <Field label="Visible du"><input type="date" className={inp} value={(f.starts_at || "").slice(0, 10)} onChange={e => set("starts_at", e.target.value)} data-testid="intranet-form-starts"/></Field>
            <Field label="Jusqu'au"><input type="date" className={inp} value={(f.ends_at || "").slice(0, 10)} onChange={e => set("ends_at", e.target.value ? e.target.value + "T23:59:59" : "")} data-testid="intranet-form-ends"/></Field>
          </>)}
          <Field label="Résumé / accroche" className="sm:col-span-2"><input className={inp} value={f.summary || ""} onChange={e => set("summary", e.target.value)} data-testid="intranet-form-summary"/></Field>
          <Field label="Contenu" className="sm:col-span-2"><textarea rows={kind === "news" ? 8 : 4} className={inp} value={f.body || ""} onChange={e => set("body", e.target.value)} data-testid="intranet-form-body"/></Field>
          {kind !== "comm" && kind !== "team" && (
            <Field label="Image de couverture" className="sm:col-span-2">
              <PhotoUploader value={f.cover_url ? [f.cover_url] : []} onChange={arr => set("cover_url", arr[arr.length - 1] || "")} max={1} testid="intranet-form-cover"/>
            </Field>
          )}
          <Field label="Lien (optionnel)" className="sm:col-span-2"><input className={inp} value={f.link || ""} onChange={e => set("link", e.target.value)} placeholder="/documents ou https://…" data-testid="intranet-form-link"/></Field>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.published} onChange={e => set("published", e.target.checked)} data-testid="intranet-form-published"/> Publié</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.pinned} onChange={e => set("pinned", e.target.checked)} data-testid="intranet-form-pinned"/> Épinglé (à la une)</label>
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100">Annuler</button>
          <button onClick={save} disabled={saving} data-testid="intranet-form-save"
                  className="px-4 py-2 rounded-lg bg-[#1d4ed8] hover:bg-blue-800 text-white text-sm font-semibold flex items-center gap-1.5 disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin"/>}{isEdit ? "Enregistrer" : "Créer"}
          </button>
        </div>
      </div>
    </div>
  );
}
